import React, { useState, useEffect, useMemo } from "react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { ENTITIES } from "../../services/contractService";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell
} from "recharts";
import { getChartColors, getThemeAwareColor } from "../../config/colorConfig";

// Compare only the date portion (YYYY-MM-DD) to avoid timezone shift issues.
// SharePoint returns UTC ISO strings; we just slice the date part for comparison.
const toDateStr = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d.slice(0, 10); // "2026-03-11T07:00:00Z" → "2026-03-11"
  const iso = d.toISOString();
  return iso.slice(0, 10);
};

const startOf = (d) => toDateStr(d); // returns "YYYY-MM-DD" string
const endOf   = (d) => toDateStr(d); // returns "YYYY-MM-DD" string

const inRange = (dateStr, from, to) => {
  const d = toDateStr(dateStr);
  if (!d) return false;
  return d >= from && d <= to;
};

const getPeriodRange = (period, customStart, customEnd) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  const pad = (n) => String(n).padStart(2, "0");
  const ymd = (yr, mo, dy) => `${yr}-${pad(mo + 1)}-${pad(dy)}`;
  const lastDay = (yr, mo) => new Date(yr, mo + 1, 0).getDate();

  switch (period) {
    case "this_month":
      return { from: ymd(y, m, 1), to: ymd(y, m, lastDay(y, m)) };
    case "last_month": {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      return { from: ymd(ly, lm, 1), to: ymd(ly, lm, lastDay(ly, lm)) };
    }
    case "this_quarter":
      return { from: ymd(y, q * 3, 1), to: ymd(y, q * 3 + 2, lastDay(y, q * 3 + 2)) };
    case "last_quarter": {
      const lq = q === 0 ? 3 : q - 1;
      const ly = q === 0 ? y - 1 : y;
      return { from: ymd(ly, lq * 3, 1), to: ymd(ly, lq * 3 + 2, lastDay(ly, lq * 3 + 2)) };
    }
    case "this_year":
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case "last_year":
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case "custom":
      return {
        from: customStart || `${y}-01-01`,
        to:   customEnd   || `${y}-12-31`,
      };
    default:
      return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
};

const fmtCurrency = (n) => { if (!n) return "0"; if (n >= 1_000_000) return "$"+(n/1_000_000).toFixed(1)+"M"; if (n >= 1_000) return "$"+(n/1_000).toFixed(0)+"K"; return "$"+n.toFixed(0); };
const fmtDays = (n) => (n === null || n === undefined || isNaN(n)) ? "—" : n.toFixed(1)+"d";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-950 border border-slate-700 rounded-lg p-3.5 text-base">
      <p className="text-slate-100 m-0 mb-1.5 font-semibold">{label}</p>
      {payload.map((p,i) => <p key={i} style={{ color:p.color }} className="m-0.5">{p.name}: <strong>{p.value}</strong></p>)}
    </div>
  );
};

const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-950 border border-slate-700 rounded-lg p-3.5 text-base">
      <p className="text-slate-100 m-0 mb-1.5 font-semibold">{label}</p>
      {payload.map((p,i) => <p key={i} style={{ color:p.color }} className="m-0.5">{p.name}: <strong>{fmtCurrency(p.value)}</strong></p>)}
    </div>
  );
};

const StatCard = ({ label, value, sub, color, theme }) => (
  <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 flex-1 min-w-40">
    <div className="text-base text-slate-400 uppercase tracking-wider font-semibold mb-1">{label}</div>
    <div style={{ color: color || theme.accent }} className="text-3xl font-bold font-mono leading-tight">{value}</div>
    {sub && <div className="text-base text-slate-400 mt-1">{sub}</div>}
  </div>
);

export const Analytics = () => {
  const { service } = useApp();
  const { theme } = useTheme();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("this_year");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Theme-aware colors
  const chartColors = getChartColors(theme);
  const ENTITY_COLORS = [chartColors.primary, "#4c8bc9", "#4cc98b", "#c94c6e"];
  const gold = chartColors.primary;

  useEffect(() => {
    service.getListItems("Contracts").then(d => {
      const all = (d?.value?.map(i => ({ ...i.fields, id:i.id, _created: i.fields.RequestDate || i.fields.Created })) || [])
        .filter(c => !c.ContractType || !c.ContractType.includes("Addendum"));
      setContracts(all);
    }).catch(() => setContracts([])).finally(() => setLoading(false));
  }, []);

  const { from, to } = useMemo(() => getPeriodRange(period, customStart, customEnd), [period, customStart, customEnd]);

  const filtered = useMemo(() =>
    contracts.filter(c => inRange(c.EffectiveDate || c._created, from, to)),
    [contracts, from, to]
  );

  console.log(contracts,filtered,from,to);
  const contractsByEntity = useMemo(() =>
    ENTITIES.map((e, i) => ({
      entity: e.split(" ")[0],
      fullName: e,
      count: filtered.filter(c => c.Entity === e).length,
      color: ENTITY_COLORS[i % ENTITY_COLORS.length],
    })).filter(r => r.count > 0),
    [filtered]
  );

  const tatByEntity = useMemo(() =>
    ENTITIES.map((e, i) => {
      const subset = filtered.filter(c => c.Entity === e && c.EffectiveDate && c._created);
      const avg = subset.length
        ? subset.reduce((sum, c) => sum + (new Date(c.EffectiveDate) - new Date(c._created)) / 86400000, 0) / subset.length
        : 0;
      return { entity: e.split(" ")[0], fullName: e, tat: parseFloat(avg.toFixed(1)), color: ENTITY_COLORS[i % ENTITY_COLORS.length], count: subset.length };
    }).filter(r => r.count > 0),
    [filtered]
  );

  const years = useMemo(() => [...new Set(contracts.map(c => c.Year).filter(Boolean))].sort(), [contracts]);

  const ceilingByYear = useMemo(() =>
    years.map(y => {
      const row = { year: String(y) };
      ENTITIES.forEach((e) => {
        row[e.split(" ")[0]] = contracts
          .filter(c => c.Year === y && c.Entity === e)
          .reduce((sum, c) => sum + (parseFloat(c.CeilingValueAnnual) || 0), 0);
      });
      return row;
    }),
    [contracts, years]
  );

  const avgTAT = useMemo(() => {
    const w = filtered.filter(c => c.EffectiveDate && c._created);
    return w.length ? w.reduce((s,c) => s + (new Date(c.EffectiveDate) - new Date(c._created)) / 86400000, 0) / w.length : null;
  }, [filtered]);

  const totalCeiling = filtered.reduce((s,c) => s + (parseFloat(c.CeilingValueAnnual)||0), 0);

  const PERIODS = [
    { value:"this_month", label:"This Month" }, { value:"last_month", label:"Last Month" },
    { value:"this_quarter", label:"This Quarter" }, { value:"last_quarter", label:"Last Quarter" },
    { value:"this_year", label:"This Year" }, { value:"last_year", label:"Last Year" },
    { value:"custom", label:"Custom Range" },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-slate-700 border-t-yellow-600 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-400">Loading analytics...</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-slate-100 mb-1">Analytics</h1>
        <p className="text-slate-400 m-0">Contract performance and spend overview</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      {/* Period Selector */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mb-6 flex items-center gap-4 flex-wrap">
        <span className="text-base text-slate-400 uppercase tracking-wider font-semibold">Period</span>
        <select value={period} onChange={e => setPeriod(e.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 cursor-pointer">
          {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {period === "custom" && (
          <>
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
              style={{ colorScheme:"dark" }}
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
              style={{ colorScheme:"dark" }}
            />
          </>
        )}
        <span className="ml-auto text-base text-slate-400">
          {from} — {to}
        </span>
      </div>

      {/* Summary Stats */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <StatCard label="Total Contracts" value={filtered.length} sub="in selected period" theme={theme} />
        <StatCard label="Active" value={filtered.filter(c=>c.Status==="Active").length} sub="currently active" color="#4cc98b" theme={theme} />
        <StatCard label="Avg Turnaround" value={fmtDays(avgTAT)} sub="request date → effective date" color="#4c8bc9" theme={theme} />
        <StatCard label="Total Annual Ceiling" value={fmtCurrency(totalCeiling)} sub="in selected period" color="#c94c6e" theme={theme} />
      </div>

      {/* Contracts Per Entity */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mb-6">
        <div className="mb-4">
          <div className="text-lg font-semibold text-slate-100 font-serif">Contracts by Entity</div>
          <div className="text-base text-slate-400">Number of contracts per entity in selected period</div>
        </div>
        {contractsByEntity.length === 0
          ? <div className="text-center text-slate-400 p-10">No contracts in this period</div>
          : <ResponsiveContainer width="100%" height={280}>
              <BarChart data={contractsByEntity} margin={{ top:8, right:16, left:0, bottom:8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                <XAxis dataKey="entity" tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Contracts" radius={[6,6,0,0]}>
                  {contractsByEntity.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
        }
      </div>

      {/* TAT Per Entity */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mb-6">
        <div className="mb-4">
          <div className="text-lg font-semibold text-slate-100 font-serif">Average Turnaround Time by Entity</div>
          <div className="text-base text-slate-400">Average days from contract submission to effective date</div>
        </div>
        {tatByEntity.length === 0
          ? <div className="text-center text-slate-400 p-10">No turnaround data for this period</div>
          : <ResponsiveContainer width="100%" height={280}>
              <BarChart data={tatByEntity} margin={{ top:8, right:16, left:0, bottom:8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                <XAxis dataKey="entity" tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} unit="d" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="tat" name="Avg Days" radius={[6,6,0,0]}>
                  {tatByEntity.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
        }
      </div>

      {/* Annual Ceiling Per Year Per Entity */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
        <div className="mb-4">
          <div className="text-lg font-semibold text-slate-100 font-serif">Annual Ceiling by Entity & Year</div>
          <div className="text-base text-slate-400">Cumulative annual ceiling value per entity across all contract years</div>
        </div>
        {ceilingByYear.length === 0
          ? <div className="text-center text-slate-400 p-10">No ceiling data available</div>
          : <ResponsiveContainer width="100%" height={320}>
              <BarChart data={ceilingByYear} margin={{ top:8, right:16, left:20, bottom:8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                <XAxis dataKey="year" tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:chartColors.text, fontSize:15 }} axisLine={false} tickLine={false} tickFormatter={fmtCurrency} />
                <Tooltip content={<CurrencyTooltip />} />
                <Legend wrapperStyle={{ color:chartColors.text, fontSize:15 }} />
                {ENTITIES.map((e, i) => (
                  <Bar key={e} dataKey={e.split(" ")[0]} name={e} stackId="a" fill={ENTITY_COLORS[i]}
                    radius={i === ENTITIES.length - 1 ? [6,6,0,0] : [0,0,0,0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
        }
      </div>
    </div>
  );
};